import { coordinate, formatEventDate, formatEventTime } from 'utils/eventContract'
import React, {useEffect, useState, useRef} from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux' 

import useToggleDropdown from 'hooks/useToggleDropdown'
import { getGoogleCalendarUrl, generateICS } from './calendarUtils'
import { BsCalendar2Plus } from "react-icons/bs";
import { SiGooglecalendar } from "react-icons/si";
import { RiAppleLine } from "react-icons/ri";
import { PiMicrosoftOutlookLogoThin } from "react-icons/pi";
import { sendResponse, setRsvpData, setViewRsvpModal } from 'features/events/eventSlice'
import { BiMailSend } from "react-icons/bi";
import SpinnerSmall from 'components/SpinnerSmall'
import {  Tooltip } from '@mui/material'
import { LuMailCheck } from "react-icons/lu";
import { pBtn, pBtnSmall } from 'components/main.styled'
import { GoPeople } from "react-icons/go";
import { FiClock } from "react-icons/fi";
import { useTranslation } from 'react-i18next'
import { useAppLanguage } from 'i18n/useAppLanguage'

const getTimeZoneAbbreviation = (timeZone, date, locale) => {
  if (!timeZone || !date) return ''
  try {
    const dateValue = new Date(`${String(date).slice(0, 10)}T12:00:00.000Z`)
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(dateValue).find((part) => part.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}


export default function Details() {
  const { t: translate } = useTranslation()
  const { currentLocale } = useAppLanguage()

  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { singlePageEventData, isResponseLoading } = useSelector((state) => state.events)
  const calMenuRef = useRef(null)
  const [coordinates, setCoordinates] = useState(null)  
  const [totalAttending, setTotalAttending] = useState(singlePageEventData.attending.length)
  const [isDropped, setIsDropped] = useToggleDropdown(calMenuRef)
  const handleDropDown = () => { setIsDropped.toggle(calMenuRef) }
 const [attending, setAttending] = useState(false)
  const hasEndDate = Boolean(singlePageEventData.endDate)
  const startTimeZone = getTimeZoneAbbreviation(singlePageEventData.timeZone, singlePageEventData.startDate, currentLocale)
  const endTimeZone = getTimeZoneAbbreviation(singlePageEventData.timeZone, singlePageEventData.endDate, currentLocale)

  const [myEventStatus, setMyEventStatus] = useState({
      statusLoading: false,
      isAttending: false,
      isInterested: false,
      isNotAttending: false,
      label: '',
  })

    const handleResponse = (response) => {
        dispatch(sendResponse({
            eventId: singlePageEventData._id,
            response: response
        }))
    }

  useEffect(() => {
    const latitude = coordinate(singlePageEventData?.coordinates?.lat, 90)
    const longitude = coordinate(singlePageEventData?.coordinates?.lng, 180)
    if(
      singlePageEventData?.eventFormat !== 'online'
      && latitude !== null
      && longitude !== null
    ){
      setCoordinates([latitude.toString(), longitude.toString()])
    }else{
      setCoordinates(null)
    }
  }, [singlePageEventData])

  useEffect(() => {
    // update the number of people attending by looping through the attending array
    // and adding the attending number of each user
    if(singlePageEventData && singlePageEventData.attending){
        let total = 0
        singlePageEventData.attending.forEach((item) => {
            total += item.attending
        })

        setTotalAttending(total)

        
    }
}, [singlePageEventData])

    const handleGoogleCalendarClick = () => {
      const url = getGoogleCalendarUrl(singlePageEventData);
      window.open(url, '_blank');
    };

    const handleDownloadICS = () => {
      const icsData = generateICS(singlePageEventData);
      const blob = new Blob([icsData], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${singlePageEventData.eventName}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    };

    const handleRsvp = (response) => {

        dispatch(setRsvpData({
            eventId: singlePageEventData._id,
            status: attending ? 'attending' : 'notAttending'
        }))        
        
        dispatch(setViewRsvpModal(true))
    }


    return (
      <DetailsContainer> 
        {
          singlePageEventData.allowRsvp && (
            <EventDetailsTop>
              <div style={{display: "flex", flexDirection: "row", gap: "10px", alignItems: "center", fontSize: "14px"}}>
                <GoPeople size={22} />
                {translate('events:detail.going', { count: totalAttending || singlePageEventData.attending.length })}
                
              </div>  
               {
                user && (
                    <MyEventStatus>
                        {
                            (user._id !== singlePageEventData.creator && singlePageEventData.allowRsvp) &&
                            (
                                attending ? 
                                (
                                    <Tooltip title={translate('events:rsvp.attendingHelp')} placement='top'>
                                        <RSVPBtn spinIcon={'none'} bg={'green'} disabled={isResponseLoading} onClick={() => handleRsvp('attending')}>
                                            {
                                                isResponseLoading ? (
                                                    <SpinnerSmall  />
                                                ) : (
                                                    <>
                                                        <LuMailCheck style={{marginRight: '10px'}} />
                                                        {translate('events:actions.going')}
                                                    </>  
                                                ) 
                                            }
                                        </RSVPBtn>
                                    </Tooltip>
                                )
                                :
                                (
                                    <RSVPBtn spinIcon={'spin 0.5s'} disabled={isResponseLoading} bg={'var(--themeColor)'}
                                        onClick={() => handleRsvp('attending')}
                                        >
                                        {
                                            isResponseLoading ? (
                                                <SpinnerSmall  />
                                            ) : (
                                                <>
                                                    <BiMailSend style={{marginRight: '10px'}} />
                                                    {translate('events:actions.rsvp')}
                                                </>  
                                            ) 
                                        }
                                    </RSVPBtn>
                                )
                            )
                        } 
                    </MyEventStatus>
                )
            }  
              </EventDetailsTop>            
            )
          }     
        <EventDescription>
          {singlePageEventData.startDate && (
            <ScheduleCard>
              <ScheduleGrid $hasEndDate={hasEndDate}>
                <ScheduleBlock>
                  <ScheduleLabel>{translate('events:create.start')}</ScheduleLabel>
                  <ScheduleDate>{formatEventDate(singlePageEventData.startDate, currentLocale, translate('events:detail.dateToBeAnnounced'))}</ScheduleDate>
                  {singlePageEventData.startTime && (
                    <ScheduleTime>
                      <FiClock />
                      <span>{formatEventTime(singlePageEventData.startTime, currentLocale)}</span>
                      {startTimeZone && <TimeZoneAbbreviation>{startTimeZone}</TimeZoneAbbreviation>}
                    </ScheduleTime>
                  )}
                </ScheduleBlock>
                {hasEndDate && (
                  <ScheduleBlock>
                    <ScheduleLabel>{translate('events:create.end')}</ScheduleLabel>
                    <ScheduleDate>{formatEventDate(singlePageEventData.endDate, currentLocale, translate('events:detail.dateToBeAnnounced'))}</ScheduleDate>
                    {singlePageEventData.endTime && (
                      <ScheduleTime>
                        <FiClock />
                        <span>{formatEventTime(singlePageEventData.endTime, currentLocale)}</span>
                        {endTimeZone && <TimeZoneAbbreviation>{endTimeZone}</TimeZoneAbbreviation>}
                      </ScheduleTime>
                    )}
                  </ScheduleBlock>
                )}
              </ScheduleGrid>
              <ScheduleActions>
                <AddToCalendarMenu ref={calMenuRef}>
                  <AddToCalendarButton
                    type="button"
                    onClick={handleDropDown}
                    aria-expanded={isDropped}
                    aria-haspopup="menu"
                  >
                    <BsCalendar2Plus size={15} /> {translate('events:actions.addToCalendar')}
                  </AddToCalendarButton>
                  {isDropped && (
                    <CalMenu role="menu">
                      <CalMenuItem type="button" role="menuitem" onClick={handleGoogleCalendarClick}>
                        <SiGooglecalendar /> {translate('events:actions.googleCalendar')}
                      </CalMenuItem>
                      <CalMenuItem type="button" role="menuitem" onClick={handleDownloadICS}>
                        <RiAppleLine /> {translate('events:actions.appleCalendar')}
                      </CalMenuItem>
                      <CalMenuItem type="button" role="menuitem" onClick={handleDownloadICS}>
                        <PiMicrosoftOutlookLogoThin /> {translate('events:actions.microsoftCalendar')}
                      </CalMenuItem>
                    </CalMenu>
                  )}
                </AddToCalendarMenu>
              </ScheduleActions>
            </ScheduleCard>
          )}
          <EventDescriptionText hasMap={coordinates ? true : false} >  
            {singlePageEventData.description.split('\n').map((subtext, index) => (
              <span style={{ display: 'flex',  marginTop: '10px' }} >
                  {subtext}  
              </span>
            ))} 
          </EventDescriptionText> 
        </EventDescription>
      </DetailsContainer>
    )
}

const RSVPBtn = styled(pBtn)`
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
    color: var(--lightThemeFontColor); 
    background-color: #0a6f14b1;
    color: white;
    font-size: 13px;
    svg{
        color: white;
        font-size: 16px;
    }

    &:hover{
        background-color: #0a6f14;

        svg{
            animation: ${props => props.spinIcon}; 
        }
    }
`


const MyEventStatus = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center; 
    cursor: pointer;
    color: var(--lightThemeFontColor);
`

const AddToCalendarMenu = styled.div`
  position: relative;
  flex-shrink: 0;
`

const AddToCalendarButton = styled(pBtnSmall)` 
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(29, 125, 132, 0.2);
  border-radius: 10px;
  background: rgba(29, 125, 132, 0.09);
  color: var(--themeColor);
  font-size: 12px;
  font-weight: 700;
  gap: 7px;

  &:hover {
    background: rgba(29, 125, 132, 0.16);
  }

`

const CalMenu = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  margin-top: 7px;
  top: 100%;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e1e7e9;
  border-radius: 12px;
  gap: 2px;
  padding: 6px;
  min-width: 190px;
  box-shadow: 0 12px 30px rgba(24, 39, 42, 0.15);
  z-index: 1000;
`

const CalMenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  background: transparent;
  white-space: nowrap;
  border-radius: 8px;
  font-size: 12px;
  gap: 10px;
  color: var(--lightThemeFontColor);
  cursor: pointer;

  svg {
    font-size: 16px;
  }

  &:hover {
    background-color: #f1f5f5;
  }
`

const ScheduleCard = styled.section`
  width: 100%;
  margin: 14px 0 4px;
  padding: 16px;
  border: 1px solid #e1e7e9;
  border-radius: 16px;
  background: linear-gradient(145deg, #ffffff 0%, #f7fafa 100%);
  box-shadow: 0 7px 20px rgba(24, 39, 42, 0.06);
`

const ScheduleActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e7ecee;

  @media (max-width: 520px) {
    ${AddToCalendarMenu}, ${AddToCalendarButton} {
      width: 100%;
    }

    ${AddToCalendarButton} {
      justify-content: center;
    }
  }
`

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasEndDate }) => $hasEndDate ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const ScheduleBlock = styled.div`
  min-width: 0;
  padding: 12px 13px;
  border: 1px solid #e7ecee;
  border-radius: 12px;
  background: #ffffff;
`

const ScheduleLabel = styled.span`
  display: block;
  margin-bottom: 5px;
  color: var(--themeColor);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const ScheduleDate = styled.strong`
  display: block;
  color: var(--lightThemeFontColor);
  font-size: 14px;
  line-height: 1.35;
`

const ScheduleTime = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  color: #667276;
  font-size: 12px;
  line-height: 1.3;

  svg {
    flex-shrink: 0;
    color: var(--themeColor);
  }
`

const TimeZoneAbbreviation = styled.small`
  color: #798589;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: .03em;
`

const DetailsContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;  

  @media (max-width: 768px) {
    height: fit-content;
  }
`
const EventDetailsTop = styled.div`
  width: 100%;
  height: fit-content; 
  border-bottom: 1px solid #d6d6d6;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: var(--lightThemeFontColor);
  padding-bottom: 10px;
`

const EventDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  height: fit-content;  
  width: 100%; 
`

const EventDescriptionText = styled.div`
  /* height: ${props => props.hasMap ? '50%' : '100%'}; */
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  font-size: 15px;
  font-weight: 400;
  color: var(--lightThemeFontColor);
  margin-bottom: 10px;
  padding-top: 20px;
  width: 100%;
`
